import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Text } from 'src/design/atoms/Text';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { TextInput } from 'src/design/atoms/TextInput';
import { Combobox, useCombobox } from 'src/design/atoms/Combobox';
import { IconLink, IconPencil, IconX } from '@tabler/icons-react';
import React from 'react';
import { useServiceWorkOrderSearch } from 'src/api/queries/serviceWorkOrders/useServiceWorkOrderSearch';
import { formatServiceJobLabel } from 'src/utils';

interface Props {
  value?: string;
  snapshotLabel?: string;
  serviceLocation?: string;
  disabled?: boolean;
  onChange: (
    id: string | undefined,
    description?: string,
    location?: string,
  ) => void;
}

const MANUAL_SENTINEL = '__manual__';

const OptionLine: React.FC<{ label: string; status?: string }> = ({
  label,
  status,
}) => (
  <Group gap={4} wrap="nowrap">
    <IconLink color="black" size={13} style={{ flexShrink: 0, opacity: 0.6 }} />
    <Text
      size="xs"
      c="black"
      component="span"
      truncate
      title={label}
      style={{ minWidth: 0 }}
    >
      {label}
    </Text>
    {status && (
      <Badge size="xs" color="gray" variant="light" style={{ flexShrink: 0 }}>
        {status}
      </Badge>
    )}
  </Group>
);

const CLOSED_STATUSES = new Set(['CLOSED', 'COMPLETE']);

/** Great Plains returns fixed-length CHAR columns, so trim/uppercase before comparing. */
const getClosedStatusBadge = (
  statusOfCall: string | undefined,
): string | undefined => {
  const normalized = statusOfCall?.trim().toUpperCase();
  return normalized && CLOSED_STATUSES.has(normalized) ? normalized : undefined;
};

export const ServiceWorkOrderSelect: React.FC<Props> = ({
  value,
  snapshotLabel,
  serviceLocation,
  disabled,
  onChange,
}) => {
  // A manual row persists as a freeform description with no linked serviceCallId,
  // so seed manual mode from props to surface it when a saved timesheet reloads.
  const isManualRow = !value && !!snapshotLabel;
  const [manualMode, setManualMode] = React.useState(() => isManualRow);
  const [manualText, setManualText] = React.useState(() =>
    isManualRow ? (snapshotLabel as string) : '',
  );
  const [searchValue, setSearchValue] = React.useState('');
  const combobox = useCombobox({
    // Discard unsubmitted typed text on close so a non-matching entry can't
    // linger in the input and masquerade as a saved selection.
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearchValue('');
    },
  });

  const select = useServiceWorkOrderSearch(
    value,
    snapshotLabel,
    serviceLocation,
  );

  const resolvedItem = value ? select.resolveItem(value) : undefined;
  const selectedValueLabel = value
    ? formatServiceJobLabel({
        jobNumber: value,
        address: resolvedItem?.adrscode || serviceLocation,
        jobName: resolvedItem ? resolvedItem.serviceDescription : snapshotLabel,
      })
    : '';

  const switchToManual = () => {
    setManualMode(true);
    setManualText('');
    // Clear linked selection
    onChange(undefined, '', undefined);
    combobox.closeDropdown();
  };

  const revertToSelect = () => {
    setManualMode(false);
    setManualText('');
    // Clear manual text
    onChange(undefined, '', undefined);
  };

  // Read-only display: muted/italic for manual (freeform snapshotLabel with no value)
  if (disabled) {
    if (isManualRow) {
      return (
        <Text size="xs" c="dimmed" fs="italic">
          {snapshotLabel}
        </Text>
      );
    }
    return <Text size="xs">{selectedValueLabel}</Text>;
  }

  if (manualMode) {
    return (
      <Group gap="xs" wrap="nowrap">
        <TextInput
          size="xs"
          placeholder="Enter description..."
          value={manualText}
          disabled={disabled}
          onChange={(e) => {
            const text = e.currentTarget.value;
            setManualText(text);
            onChange(undefined, text, undefined);
          }}
          style={{ flex: 1 }}
        />
        {!disabled && (
          <BasicActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            onClick={revertToSelect}
            aria-label="Revert to work-order select"
            title="Revert to work-order select"
          >
            <IconX size={12} />
          </BasicActionIcon>
        )}
      </Group>
    );
  }

  // Active linked/unselected mode: Combobox
  const options = select.data
    .filter((o) => o.value !== '__more_results__')
    .map((o) => {
      const item = select.resolveItem(o.value);
      return (
        <Combobox.Option key={o.value} value={o.value} disabled={o.disabled}>
          {item ? (
            <OptionLine
              label={o.label}
              status={getClosedStatusBadge(item.statusOfCall)}
            />
          ) : (
            // Snapshot-preserved option (not in current result set)
            <Text size="xs" truncate title={o.label}>
              {o.label}
            </Text>
          )}
        </Combobox.Option>
      );
    });

  const hasMore = select.data.some((o) => o.value === '__more_results__');

  return (
    <Combobox
      store={combobox}
      width="max-content"
      onOptionSubmit={(val) => {
        if (val === MANUAL_SENTINEL) {
          switchToManual();
          return;
        }
        if (select.isMoreMarker(val)) return;
        const item = select.resolveItem(val);
        if (item) {
          onChange(
            val,
            item.serviceDescription ?? undefined,
            item.adrscode ?? undefined,
          );
        } else {
          // Snapshot item
          onChange(val, snapshotLabel, serviceLocation);
        }
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <TextInput
          size="xs"
          placeholder="Select service call..."
          value={value ? selectedValueLabel : searchValue}
          rightSection={select.isFetching ? <Loader size="xs" /> : undefined}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setSearchValue(v);
            select.onSearchChange(v);
            // If the user is editing the input, clear the current selection
            if (value) {
              onChange(undefined, '', undefined);
            }
            combobox.openDropdown();
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown style={{ maxWidth: 'min(720px, 90vw)' }}>
        <Combobox.Options>
          <div
            data-testid="swo-options-scroll"
            style={{
              maxHeight: 320,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            {options.length === 0 && !select.isFetching ? (
              <Combobox.Empty>{select.nothingFoundMessage}</Combobox.Empty>
            ) : (
              options
            )}
            {hasMore && (
              <Combobox.Option value="__more_results__" disabled>
                <Text size="xs" c="dimmed">
                  Refine search to see more.
                </Text>
              </Combobox.Option>
            )}
          </div>
        </Combobox.Options>
        {/* Pinned footer — always visible */}
        <Box
          style={{
            borderTop: '1px solid var(--mantine-color-gray-2)',
            paddingTop: 4,
          }}
        >
          <Combobox.Option value={MANUAL_SENTINEL}>
            <Group gap={4} wrap="nowrap">
              <IconPencil size={13} style={{ flexShrink: 0, opacity: 0.6 }} />
              <Text size="xs">Click to enter description manually</Text>
            </Group>
          </Combobox.Option>
        </Box>
      </Combobox.Dropdown>
    </Combobox>
  );
};
